import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ReactPaginate from "react-paginate";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  CardTitle,
  Col,
  Container,
  FormGroup,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  Progress,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import OnlyHeader from "components/Headers/OnlyHeader";
import {
  getChatbotFeedbackSummary,
  getChatbotAuditLogs,
} from "store/actions/chatbotKnowledgeAction";

const DAY_OPTIONS = [
  { value: 7, label: "Last 7 Days" },
  { value: 14, label: "Last 14 Days" },
  { value: 30, label: "Last 30 Days" },
  { value: 60, label: "Last 60 Days" },
  { value: 90, label: "Last 90 Days" },
  { value: 180, label: "Last 180 Days" },
  { value: 365, label: "Last 1 Year (365 Days)" },
];

/**
 * Safely convert any value to string to prevent React "Objects are not valid as child" errors.
 */
const safeString = (val, fallback = "") => {
  if (val === null || val === undefined) return fallback;
  if (typeof val === "string") return val;
  if (typeof val === "number" || typeof val === "boolean") return String(val);
  if (typeof val === "object") {
    if (typeof val.question === "string" && val.question) return val.question;
    if (typeof val.reply === "string" && val.reply) return val.reply;
    if (typeof val.content === "string" && val.content) return val.content;
    if (typeof val.feedback === "string" && val.feedback) {
      const msgPart =
        typeof val.message === "string" && val.message
          ? ` (Message: ${val.message})`
          : "";
      return `Rating: ${val.feedback}${msgPart}`;
    }
    if (typeof val.message === "string" && val.message) return val.message;
    try {
      return JSON.stringify(val);
    } catch {
      return fallback;
    }
  }
  return fallback;
};

const ChatbotFeedback = () => {
  const dispatch = useDispatch();

  const {
    feedbackSummary,
    feedbackLoader,
    auditLogs,
    auditLoader,
    auditTotalPages,
    auditCurrentPage,
  } = useSelector((state) => state.chatbotKnowledge);

  // Filter & tab states
  const [selectedDays, setSelectedDays] = useState(30);
  const [activeTab, setActiveTab] = useState("recentNotHelpful"); // 'recentNotHelpful' | 'audit'

  // Not helpful search/filter within the list
  const [notHelpfulSearch, setNotHelpfulSearch] = useState("");

  // Audit filters & pagination
  const [auditSessionFilter, setAuditSessionFilter] = useState("");
  const [appliedAuditSession, setAppliedAuditSession] = useState("");
  const [auditPage, setAuditPage] = useState(1);
  const auditLimit = 20;

  // Detail Modal state
  const [detailModal, setDetailModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  // Load summary on mount, when days change, or when switching to recentNotHelpful tab
  useEffect(() => {
    if (activeTab === "recentNotHelpful") {
      dispatch(getChatbotFeedbackSummary(selectedDays));
    }
  }, [dispatch, activeTab, selectedDays]);

  // Load audit logs when tab is audit, or page/session filter changes
  useEffect(() => {
    if (activeTab === "audit") {
      dispatch(
        getChatbotAuditLogs({
          event: "feedback",
          session: appliedAuditSession,
          page: auditPage,
          limit: auditLimit,
        })
      );
    }
  }, [dispatch, activeTab, appliedAuditSession, auditPage]);

  const handleRefresh = () => {
    dispatch(getChatbotFeedbackSummary(selectedDays));
    if (activeTab === "audit") {
      dispatch(
        getChatbotAuditLogs({
          event: "feedback",
          session: appliedAuditSession,
          page: auditPage,
          limit: auditLimit,
        })
      );
    }
  };

  const handleAuditSearch = (e) => {
    if (e) e.preventDefault();
    setAuditPage(1);
    setAppliedAuditSession(auditSessionFilter.trim());
  };

  const handleAuditReset = () => {
    setAuditSessionFilter("");
    setAppliedAuditSession("");
    setAuditPage(1);
  };

  const handleAuditPageChange = ({ selected }) => {
    setAuditPage(selected + 1);
  };

  const handleCopyText = (text, label = "Item") => {
    const str = safeString(text);
    if (!str) return;
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(str);
      toast.info(`${label} copied to clipboard!`);
    } else {
      toast.info(`ID: ${str}`);
    }
  };

  const openDetailModal = (item) => {
    setSelectedItem(item);
    setDetailModal(true);
  };

  const closeDetailModal = () => {
    setDetailModal(false);
    setSelectedItem(null);
  };

  // Filtered recent not helpful items
  const recentNotHelpfulList = Array.isArray(feedbackSummary?.recentNotHelpful)
    ? feedbackSummary.recentNotHelpful
    : [];

  const filteredNotHelpful = recentNotHelpfulList.filter((item) => {
    if (!notHelpfulSearch.trim()) return true;
    const term = notHelpfulSearch.toLowerCase();
    const qMatch = safeString(item.question).toLowerCase().includes(term);
    const rMatch = safeString(item.reply).toLowerCase().includes(term);
    const sMatch = safeString(item.session).toLowerCase().includes(term);
    return qMatch || rMatch || sMatch;
  });

  const helpfulCount = feedbackSummary?.helpful ?? 0;
  const notHelpfulCount = feedbackSummary?.not_helpful ?? 0;
  const totalFeedback = helpfulCount + notHelpfulCount;
  const helpfulRate = feedbackSummary?.helpfulRate;

  // Rating color helper
  const getRateColor = (rate) => {
    if (rate === null || rate === undefined) return "secondary";
    if (rate >= 70) return "success";
    if (rate >= 50) return "warning";
    return "danger";
  };

  return (
    <>
      <OnlyHeader />
      <Container className="mt--7" fluid>
        {/* Top Header Card */}
        <Row className="mb-4">
          <Col xs="12">
            <Card className="shadow">
              <CardBody className="py-3 px-3 px-sm-4">
                <Row className="align-items-center justify-content-between">
                  <Col xs="12" md="6" className="mb-3 mb-md-0">
                    <h2
                      className="mb-1 text-tealpot font-weight-bold"
                      style={{ fontSize: "1.35rem" }}
                    >
                      <i className="fas fa-robot mr-2 text-tealpot" />
                      Chatbot Feedback Analytics
                    </h2>
                    <p className="text-muted text-sm mb-0">
                      Monitor satisfaction rates and review negative chatbot responses.
                    </p>
                  </Col>
                  <Col
                    xs="12"
                    md="6"
                    className="d-flex align-items-center justify-content-start justify-content-md-end"
                  >
                    <div
                      className="d-flex align-items-center flex-wrap flex-sm-nowrap w-100 w-md-auto justify-content-start justify-content-md-end"
                      style={{ gap: "8px" }}
                    >
                      <div className="d-flex align-items-center flex-grow-1 flex-sm-grow-0">
                        <span className="text-sm font-weight-bold text-muted mr-2 text-nowrap">
                          <i className="fas fa-calendar-alt mr-1" />
                          Period:
                        </span>
                        <Input
                          type="select"
                          bsSize="sm"
                          value={selectedDays}
                          onChange={(e) => setSelectedDays(Number(e.target.value))}
                          disabled={feedbackLoader}
                          className="flex-grow-1 flex-sm-grow-0"
                          style={{ minWidth: "135px", maxWidth: "180px", height: "38px" }}
                        >
                          {DAY_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </Input>
                      </div>
                      <Button
                        color="secondary"
                        size="sm"
                        className="px-3 text-nowrap flex-shrink-0"
                        onClick={handleRefresh}
                        disabled={feedbackLoader || auditLoader}
                        style={{ height: "38px" }}
                      >
                        {feedbackLoader || auditLoader ? (
                          <Spinner size="sm" />
                        ) : (
                          <>
                            <i className="fas fa-sync-alt mr-1" />
                            Refresh
                          </>
                        )}
                      </Button>
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Metric Cards Row */}
        <Row className="mb-4">
          {/* Helpful Rate */}
          <Col xs="12" sm="6" xl="3" className="mb-3 mb-xl-0">
            <Card className="card-stats shadow h-100">
              <CardBody className="p-3 p-md-4">
                <div className="d-flex justify-content-between align-items-center">
                  <div style={{ minWidth: 0 }}>
                    <CardTitle
                      tag="h5"
                      className="text-uppercase text-muted mb-0 font-weight-bold text-truncate"
                    >
                      Helpful Rate
                    </CardTitle>
                    <span className="h2 font-weight-bold mb-0">
                      {feedbackLoader ? (
                        <Spinner size="sm" />
                      ) : helpfulRate !== null && helpfulRate !== undefined ? (
                        `${helpfulRate}%`
                      ) : (
                        "No ratings"
                      )}
                    </span>
                  </div>
                  <div
                    className={`icon icon-shape bg-${getRateColor(
                      helpfulRate
                    )} text-white rounded-circle shadow flex-shrink-0 ml-2`}
                  >
                    <i className="fas fa-percentage" />
                  </div>
                </div>
                <div className="mt-3">
                  {helpfulRate !== null && helpfulRate !== undefined ? (
                    <>
                      <Progress
                        value={helpfulRate}
                        color={getRateColor(helpfulRate)}
                        style={{ height: "6px" }}
                        className="mb-1"
                      />
                      <small className="text-muted d-block text-truncate">
                        {helpfulRate >= 70 ? (
                          <span className="text-success font-weight-bold">
                            <i className="fas fa-check-circle mr-1" /> High satisfaction
                          </span>
                        ) : helpfulRate >= 50 ? (
                          <span className="text-warning font-weight-bold">
                            <i className="fas fa-exclamation-circle mr-1" /> Moderate satisfaction
                          </span>
                        ) : (
                          <span className="text-danger font-weight-bold">
                            <i className="fas fa-arrow-down mr-1" /> Needs improvement
                          </span>
                        )}
                      </small>
                    </>
                  ) : (
                    <small className="text-muted">Awaiting first rating</small>
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>

          {/* Helpful Count */}
          <Col xs="12" sm="6" xl="3" className="mb-3 mb-xl-0">
            <Card className="card-stats shadow h-100">
              <CardBody className="p-3 p-md-4">
                <div className="d-flex justify-content-between align-items-center">
                  <div style={{ minWidth: 0 }}>
                    <CardTitle
                      tag="h5"
                      className="text-uppercase text-muted mb-0 font-weight-bold text-truncate"
                    >
                      Helpful Replies
                    </CardTitle>
                    <span className="h2 font-weight-bold mb-0 text-success">
                      {feedbackLoader ? <Spinner size="sm" /> : helpfulCount}
                    </span>
                  </div>
                  <div className="icon icon-shape bg-success text-white rounded-circle shadow flex-shrink-0 ml-2">
                    <i className="fas fa-thumbs-up" />
                  </div>
                </div>
                <p className="mt-3 mb-0 text-muted text-sm text-truncate">
                  <span className="text-success font-weight-bold mr-1">
                    <i className="fas fa-arrow-up" />
                  </span>
                  <span>Positive user ratings</span>
                </p>
              </CardBody>
            </Card>
          </Col>

          {/* Not Helpful Count */}
          <Col xs="12" sm="6" xl="3" className="mb-3 mb-xl-0">
            <Card className="card-stats shadow h-100">
              <CardBody className="p-3 p-md-4">
                <div className="d-flex justify-content-between align-items-center">
                  <div style={{ minWidth: 0 }}>
                    <CardTitle
                      tag="h5"
                      className="text-uppercase text-muted mb-0 font-weight-bold text-truncate"
                    >
                      Not Helpful
                    </CardTitle>
                    <span className="h2 font-weight-bold mb-0 text-danger">
                      {feedbackLoader ? <Spinner size="sm" /> : notHelpfulCount}
                    </span>
                  </div>
                  <div className="icon icon-shape bg-danger text-white rounded-circle shadow flex-shrink-0 ml-2">
                    <i className="fas fa-thumbs-down" />
                  </div>
                </div>
                <p className="mt-3 mb-0 text-muted text-sm text-truncate">
                  <span className="text-danger font-weight-bold mr-1">
                    <i className="fas fa-flag" />
                  </span>
                  <span>Flagged for review</span>
                </p>
              </CardBody>
            </Card>
          </Col>

          {/* Total Ratings */}
          <Col xs="12" sm="6" xl="3" className="mb-3 mb-xl-0">
            <Card className="card-stats shadow h-100">
              <CardBody className="p-3 p-md-4">
                <div className="d-flex justify-content-between align-items-center">
                  <div style={{ minWidth: 0 }}>
                    <CardTitle
                      tag="h5"
                      className="text-uppercase text-muted mb-0 font-weight-bold text-truncate"
                    >
                      Total Feedback
                    </CardTitle>
                    <span className="h2 font-weight-bold mb-0 text-primary">
                      {feedbackLoader ? <Spinner size="sm" /> : totalFeedback}
                    </span>
                  </div>
                  <div className="icon icon-shape bg-primary text-white rounded-circle shadow flex-shrink-0 ml-2">
                    <i className="fas fa-comments" />
                  </div>
                </div>
                <p className="mt-3 mb-0 text-muted text-sm text-truncate">
                  <span className="text-muted font-weight-bold">
                    In last {selectedDays} days
                  </span>
                </p>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Content Card with Tabs */}
        <Row>
          <Col xs="12">
            <Card className="shadow">
              {/* Top Tabs Navigation Bar */}
              <div
                className="border-bottom px-3 px-sm-4 pt-3 bg-white"
                style={{
                  overflowX: "auto",
                  overflowY: "hidden",
                  WebkitOverflowScrolling: "touch",
                  scrollbarWidth: "none",
                  msOverflowStyle: "none",
                }}
              >
                <Nav
                  tabs
                  className="border-bottom-0 flex-nowrap"
                  style={{ minWidth: "max-content" }}
                >
                  <NavItem>
                    <NavLink
                      className={`cursor-pointer font-weight-bold py-2 px-3 text-nowrap ${
                        activeTab === "recentNotHelpful"
                          ? "active text-tealpot border-bottom-tealpot"
                          : "text-muted"
                      }`}
                      style={{
                        borderBottom:
                          activeTab === "recentNotHelpful"
                            ? "3px solid #008080"
                            : "3px solid transparent",
                        color: activeTab === "recentNotHelpful" ? "#008080" : "#8898aa",
                      }}
                      onClick={() => setActiveTab("recentNotHelpful")}
                    >
                      <i className="fas fa-exclamation-triangle mr-2 text-warning" />
                      Recent Not Helpful Replies
                      {recentNotHelpfulList.length > 0 && (
                        <Badge color="danger" pill className="ml-2">
                          {recentNotHelpfulList.length}
                        </Badge>
                      )}
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={`cursor-pointer font-weight-bold py-2 px-3 text-nowrap ${
                        activeTab === "audit"
                          ? "active text-tealpot border-bottom-tealpot"
                          : "text-muted"
                      }`}
                      style={{
                        borderBottom:
                          activeTab === "audit"
                            ? "3px solid #008080"
                            : "3px solid transparent",
                        color: activeTab === "audit" ? "#008080" : "#8898aa",
                      }}
                      onClick={() => setActiveTab("audit")}
                    >
                      <i className="fas fa-history mr-2 text-tealpot" />
                      Feedback Audit History
                    </NavLink>
                  </NavItem>
                </Nav>
              </div>

              {/* Sub-header with Title and Filter on the Right */}
              <CardHeader className="border-0 bg-white py-3 px-3 px-sm-4">
                <Row className="align-items-center justify-content-between">
                  <Col xs="12" md="6" className="mb-2 mb-md-0">
                    <h3
                      className="mb-0 text-dark font-weight-bold"
                      style={{ fontSize: "1.15rem" }}
                    >
                      {activeTab === "recentNotHelpful"
                        ? "Recent Flagged Replies"
                        : "Feedback Audit History"}
                    </h3>
                    <small className="text-muted d-block">
                      {activeTab === "recentNotHelpful"
                        ? "Review the latest negative feedback interactions"
                        : "Log of feedback submissions across chat sessions"}
                    </small>
                  </Col>

                  {/* Filter / Search Bar */}
                  <Col
                    xs="12"
                    md="6"
                    className="d-flex justify-content-start justify-content-md-end mt-2 mt-md-0"
                  >
                    {activeTab === "recentNotHelpful" ? (
                      <div className="w-100" style={{ maxWidth: "340px" }}>
                        <FormGroup className="mb-0">
                          <Input
                            bsSize="sm"
                            placeholder="Filter questions / replies..."
                            type="text"
                            value={notHelpfulSearch}
                            onChange={(e) => setNotHelpfulSearch(e.target.value)}
                            style={{ height: "38px" }}
                          />
                        </FormGroup>
                      </div>
                    ) : (
                      <div
                        className="d-flex align-items-center w-100 justify-content-start justify-content-md-end flex-nowrap"
                        style={{ maxWidth: "360px" }}
                      >
                        <FormGroup className="mb-0 mr-2 flex-grow-1" style={{ minWidth: 0 }}>
                          <Input
                            bsSize="sm"
                            placeholder="Filter by Session ID..."
                            type="text"
                            value={auditSessionFilter}
                            onChange={(e) => setAuditSessionFilter(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") handleAuditSearch(e);
                            }}
                            style={{ height: "38px" }}
                          />
                        </FormGroup>
                        <Button
                          color="primary"
                          size="sm"
                          className="mr-1 px-3 flex-shrink-0"
                          onClick={handleAuditSearch}
                          disabled={auditLoader}
                          style={{ height: "38px" }}
                        >
                          <i className="fas fa-search" />
                        </Button>
                        {appliedAuditSession && (
                          <Button
                            color="secondary"
                            size="sm"
                            className="px-3 flex-shrink-0"
                            onClick={handleAuditReset}
                            disabled={auditLoader}
                            style={{ height: "38px" }}
                          >
                            <i className="fas fa-times" />
                          </Button>
                        )}
                      </div>
                    )}
                  </Col>
                </Row>
              </CardHeader>

              {/* TAB 1: Recent Not Helpful */}
              {activeTab === "recentNotHelpful" && (
                <div className="table-responsive">
                  <Table
                    className="align-items-center table-flush"
                    hover
                    style={{ minWidth: "780px" }}
                  >
                    <thead className="thead-light">
                      <tr>
                        <th scope="col" style={{ width: "50px" }}>#</th>
                        <th scope="col" style={{ width: "150px" }}>Date & Time</th>
                        <th scope="col" style={{ width: "250px" }}>User Question</th>
                        <th scope="col">AI Assistant Reply</th>
                        <th scope="col" style={{ width: "150px" }}>Session ID</th>
                        <th scope="col" style={{ width: "110px" }} className="text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {feedbackLoader ? (
                        <tr>
                          <td colSpan="6" className="text-center py-5">
                            <Spinner size="lg" color="primary" />
                            <div className="mt-2 text-muted">Loading feedback...</div>
                          </td>
                        </tr>
                      ) : filteredNotHelpful.length > 0 ? (
                        filteredNotHelpful.map((item, idx) => {
                          const itemSession = safeString(item.session);
                          const itemQuestion = safeString(item.question);
                          const itemReply = safeString(item.reply);

                          return (
                            <tr key={itemSession || idx}>
                              <td className="font-weight-bold text-muted">{idx + 1}</td>
                              <td className="text-nowrap">
                                <small className="font-weight-bold text-dark d-block">
                                  {item.date ? dayjs(item.date).format("MMM DD, YYYY") : "N/A"}
                                </small>
                                <small className="text-muted">
                                  {item.date ? dayjs(item.date).format("hh:mm A") : ""}
                                </small>
                              </td>
                              <td>
                                <span
                                  className="font-weight-bold text-dark d-inline-block text-truncate"
                                  style={{ maxWidth: "250px" }}
                                  title={itemQuestion}
                                >
                                  {itemQuestion || <em className="text-muted">No question recorded</em>}
                                </span>
                              </td>
                              <td>
                                <div
                                  className="text-muted text-sm text-truncate"
                                  style={{ maxWidth: "420px" }}
                                  title={itemReply}
                                >
                                  {itemReply || <em className="text-muted">No reply recorded</em>}
                                </div>
                              </td>
                              <td>
                                <div className="d-flex align-items-center" style={{ minWidth: 0 }}>
                                  <span
                                    className="text-monospace text-xs text-muted text-truncate mr-1"
                                    style={{ maxWidth: "100px" }}
                                    title={itemSession}
                                  >
                                    {itemSession || "N/A"}
                                  </span>
                                  {itemSession && (
                                    <Button
                                      color="link"
                                      size="sm"
                                      className="p-0 text-primary flex-shrink-0"
                                      title="Copy Session ID"
                                      onClick={() => handleCopyText(itemSession, "Session ID")}
                                    >
                                      <i className="far fa-copy" />
                                    </Button>
                                  )}
                                </div>
                              </td>
                              <td className="text-right">
                                <Button
                                  color="primary"
                                  size="sm"
                                  className="px-2"
                                  onClick={() =>
                                    openDetailModal({
                                      session: itemSession,
                                      date: item.date,
                                      question: itemQuestion,
                                      reply: itemReply,
                                      rating: "not_helpful",
                                    })
                                  }
                                >
                                  View Details
                                </Button>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="6" className="text-center py-5">
                            <div className="mb-2">
                              <i className="fas fa-smile text-success" style={{ fontSize: "2.5rem" }} />
                            </div>
                            <h4 className="font-weight-bold mb-1">
                              {notHelpfulSearch ? "No matching negative feedback found" : "No Negative Feedback Found"}
                            </h4>
                            <p className="text-muted text-sm mb-0">
                              {notHelpfulSearch
                                ? "Try adjusting your search keywords."
                                : `No replies were marked "Not Helpful" in the selected ${selectedDays}-day period.`}
                            </p>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              )}

              {/* TAB 2: Feedback Audit History */}
              {activeTab === "audit" && (
                <>
                  <div className="table-responsive">
                    <Table
                      className="align-items-center table-flush"
                      hover
                      style={{ minWidth: "860px" }}
                    >
                      <thead className="thead-light">
                        <tr>
                          <th scope="col" style={{ width: "50px" }}>#</th>
                          <th scope="col" style={{ width: "150px" }}>Timestamp</th>
                          <th scope="col" style={{ width: "130px" }}>Rating</th>
                          <th scope="col" style={{ width: "170px" }}>User / Role</th>
                          <th scope="col" style={{ width: "150px" }}>Session ID</th>
                          <th scope="col">Details / Message</th>
                          <th scope="col" style={{ width: "100px" }} className="text-right">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {auditLoader ? (
                          <tr>
                            <td colSpan="7" className="text-center py-5">
                              <Spinner size="lg" color="primary" />
                              <div className="mt-2 text-muted">Loading audit history...</div>
                            </td>
                          </tr>
                        ) : auditLogs && auditLogs.length > 0 ? (
                          auditLogs.map((log, idx) => {
                            const detailsObj =
                              typeof log.details === "object" && log.details !== null
                                ? log.details
                                : null;
                            const metadataObj =
                              typeof log.metadata === "object" && log.metadata !== null
                                ? log.metadata
                                : null;

                            // Extract rating string safely
                            const rating =
                              (typeof log.feedback === "string" ? log.feedback : null) ||
                              (typeof log.rating === "string" ? log.rating : null) ||
                              (typeof metadataObj?.feedback === "string"
                                ? metadataObj.feedback
                                : null) ||
                              (typeof detailsObj?.feedback === "string"
                                ? detailsObj.feedback
                                : null) ||
                              (typeof log.action === "string" ? log.action : null) ||
                              "";

                            const isHelpful = rating === "helpful";
                            const isNotHelpful = rating === "not_helpful";
                            const timestamp = log.createdAt || log.date || log.timestamp;

                            // Extract sessionId safely
                            const sessionId =
                              typeof log.session === "object" && log.session !== null
                                ? safeString(log.session?._id || log.session?.id)
                                : typeof log.session === "string"
                                ? log.session
                                : "";

                            // Extract userName safely
                            let userName = "Guest User";
                            if (log.user) {
                              if (typeof log.user === "object") {
                                userName =
                                  log.user.name ||
                                  log.user.email ||
                                  (log.user.firstName
                                    ? `${log.user.firstName} ${log.user.lastName || ""}`.trim()
                                    : "") ||
                                  safeString(log.user._id) ||
                                  "User";
                              } else if (typeof log.user === "string") {
                                userName = log.user;
                              }
                            } else if (log.guestToken || log.isGuest) {
                              userName = "Guest User";
                            }

                            // Extract display details safely (preventing object rendering)
                            let displayDetails = "";
                            if (typeof log.question === "string" && log.question) {
                              displayDetails = log.question;
                            } else if (
                              typeof metadataObj?.question === "string" &&
                              metadataObj.question
                            ) {
                              displayDetails = metadataObj.question;
                            } else if (
                              typeof detailsObj?.question === "string" &&
                              detailsObj.question
                            ) {
                              displayDetails = detailsObj.question;
                            } else if (typeof log.message === "string" && log.message) {
                              displayDetails = log.message;
                            } else if (
                              typeof metadataObj?.message === "string" &&
                              metadataObj.message
                            ) {
                              displayDetails = `Message: ${metadataObj.message}`;
                            } else if (
                              typeof detailsObj?.message === "string" &&
                              detailsObj.message
                            ) {
                              displayDetails = `Message: ${detailsObj.message}`;
                            } else if (typeof log.details === "string" && log.details) {
                              displayDetails = log.details;
                            } else if (detailsObj || metadataObj) {
                              displayDetails = safeString(
                                detailsObj || metadataObj,
                                "Feedback logged"
                              );
                            } else {
                              displayDetails = "Feedback logged";
                            }

                            // Question & Reply for details modal
                            const modalQuestion =
                              safeString(
                                log.question ||
                                  metadataObj?.question ||
                                  detailsObj?.question
                              ) || "N/A";
                            const modalReply =
                              safeString(
                                log.reply ||
                                  metadataObj?.reply ||
                                  detailsObj?.reply ||
                                  displayDetails
                              ) || "N/A";

                            return (
                              <tr key={log._id || log.id || idx}>
                                <td className="font-weight-bold text-muted">
                                  {(auditCurrentPage - 1) * auditLimit + idx + 1}
                                </td>
                                <td className="text-nowrap">
                                  <small className="font-weight-bold text-dark d-block">
                                    {timestamp ? dayjs(timestamp).format("MMM DD, YYYY") : "N/A"}
                                  </small>
                                  <small className="text-muted">
                                    {timestamp ? dayjs(timestamp).format("hh:mm A") : ""}
                                  </small>
                                </td>
                                <td>
                                  {isHelpful ? (
                                    <Badge color="success" pill className="px-2 py-1">
                                      <i className="fas fa-thumbs-up mr-1" />
                                      Helpful
                                    </Badge>
                                  ) : isNotHelpful ? (
                                    <Badge color="danger" pill className="px-2 py-1">
                                      <i className="fas fa-thumbs-down mr-1" />
                                      Not Helpful
                                    </Badge>
                                  ) : (
                                    <Badge color="secondary" pill className="px-2 py-1">
                                      {safeString(rating, "Rating")}
                                    </Badge>
                                  )}
                                </td>
                                <td>
                                  <span
                                    className="font-weight-bold text-dark d-block text-truncate"
                                    style={{ maxWidth: "180px" }}
                                    title={userName}
                                  >
                                    {userName}
                                  </span>
                                </td>
                                <td>
                                  <div className="d-flex align-items-center" style={{ minWidth: 0 }}>
                                    <span
                                      className="text-monospace text-xs text-muted text-truncate mr-1"
                                      style={{ maxWidth: "120px" }}
                                      title={sessionId}
                                    >
                                      {sessionId || "N/A"}
                                    </span>
                                    {sessionId && (
                                      <Button
                                        color="link"
                                        size="sm"
                                        className="p-0 text-primary flex-shrink-0"
                                        title="Copy Session ID"
                                        onClick={() => handleCopyText(sessionId, "Session ID")}
                                      >
                                        <i className="far fa-copy" />
                                      </Button>
                                    )}
                                  </div>
                                </td>
                                <td>
                                  <span
                                    className="text-muted text-sm text-truncate d-inline-block"
                                    style={{ maxWidth: "320px" }}
                                    title={displayDetails}
                                  >
                                    {displayDetails}
                                  </span>
                                </td>
                                <td className="text-right">
                                  <Button
                                    color="primary"
                                    size="sm"
                                    className="px-2"
                                    onClick={() =>
                                      openDetailModal({
                                        session: sessionId,
                                        date: timestamp,
                                        question: modalQuestion,
                                        reply: modalReply,
                                        rating: rating,
                                        user: userName,
                                      })
                                    }
                                  >
                                    View
                                  </Button>
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan="7" className="text-center py-5">
                              <div className="mb-2">
                                <i className="fas fa-clipboard-list text-muted" style={{ fontSize: "2.5rem" }} />
                              </div>
                              <h4 className="font-weight-bold mb-1">No Feedback Audit Records</h4>
                              <p className="text-muted text-sm mb-0">
                                {appliedAuditSession
                                  ? `No audit entries found for session "${appliedAuditSession}".`
                                  : "Audit history will populate as users submit feedback on chatbot responses."}
                              </p>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>

                  {auditTotalPages > 1 && (
                    <CardFooter className="py-3 py-sm-4 border-0 px-3 px-sm-4">
                      <nav aria-label="...">
                        <ReactPaginate
                          previousLabel={"<"}
                          nextLabel={">"}
                          breakLabel={"..."}
                          breakClassName={"break-me"}
                          pageCount={auditTotalPages}
                          marginPagesDisplayed={1}
                          pageRangeDisplayed={2}
                          onPageChange={handleAuditPageChange}
                          containerClassName={
                            "pagination px-1 justify-content-center justify-content-sm-end flex-wrap mb-0"
                          }
                          activeClassName={"active"}
                          pageClassName={"page-item"}
                          pageLinkClassName={"page-link"}
                          previousClassName={"page-item"}
                          nextClassName={"page-item"}
                          previousLinkClassName={"page-link"}
                          nextLinkClassName={"page-link"}
                          forcePage={auditCurrentPage - 1}
                        />
                      </nav>
                    </CardFooter>
                  )}
                </>
              )}
            </Card>
          </Col>
        </Row>
      </Container>

      {/* DETAIL MODAL */}
      <Modal
        isOpen={detailModal}
        toggle={closeDetailModal}
        size="lg"
        centered
        scrollable={true}
      >
        <ModalHeader toggle={closeDetailModal} className="border-bottom py-3 px-3 px-sm-4">
          <div className="d-flex align-items-center flex-wrap" style={{ gap: "8px" }}>
            <span
              className="h4 font-weight-bold mb-0 text-tealpot"
              style={{ fontSize: "1.15rem" }}
            >
              <i className="fas fa-comment-alt mr-2" />
              Feedback Interaction Details
            </span>
            {selectedItem?.rating && (
              <Badge
                color={
                  selectedItem.rating === "helpful"
                    ? "success"
                    : selectedItem.rating === "not_helpful"
                    ? "danger"
                    : "secondary"
                }
                pill
                className="px-2 py-1 text-capitalize"
              >
                {selectedItem.rating === "helpful" ? (
                  <>
                    <i className="fas fa-thumbs-up mr-1" /> Helpful
                  </>
                ) : selectedItem.rating === "not_helpful" ? (
                  <>
                    <i className="fas fa-thumbs-down mr-1" /> Not Helpful
                  </>
                ) : (
                  safeString(selectedItem.rating)
                )}
              </Badge>
            )}
          </div>
        </ModalHeader>
        <ModalBody className="p-3 p-md-4">
          {selectedItem && (
            <>
              {/* Meta Info Bar */}
              <div className="bg-secondary p-3 rounded mb-3">
                <Row>
                  <Col xs="12" sm="6" className="mb-2 mb-sm-0">
                    <small className="text-muted text-uppercase font-weight-bold d-block">
                      Date & Time
                    </small>
                    <span className="font-weight-bold text-dark text-break">
                      {selectedItem.date
                        ? dayjs(selectedItem.date).format("MMMM DD, YYYY - hh:mm:ss A")
                        : "N/A"}
                    </span>
                  </Col>
                  <Col xs="12" sm="6">
                    <small className="text-muted text-uppercase font-weight-bold d-block">
                      Session ID
                    </small>
                    <div
                      className="d-flex align-items-center justify-content-between justify-content-sm-start"
                      style={{ minWidth: 0 }}
                    >
                      <span
                        className="text-monospace font-weight-bold text-dark text-truncate mr-2"
                        style={{ minWidth: 0, maxWidth: "100%" }}
                        title={safeString(selectedItem.session)}
                      >
                        {safeString(selectedItem.session, "N/A")}
                      </span>
                      {selectedItem.session && (
                        <Button
                          color="secondary"
                          size="sm"
                          className="p-1 px-2 flex-shrink-0"
                          onClick={() => handleCopyText(selectedItem.session, "Session ID")}
                          title="Copy Session ID"
                        >
                          <i className="far fa-copy mr-1" /> Copy
                        </Button>
                      )}
                    </div>
                  </Col>
                  {selectedItem.user && (
                    <Col xs="12" className="mt-2 pt-2 border-top">
                      <small className="text-muted text-uppercase font-weight-bold d-block">
                        User / Role
                      </small>
                      <span className="font-weight-bold text-dark text-break">
                        {selectedItem.user}
                      </span>
                    </Col>
                  )}
                </Row>
              </div>

              {/* User Question */}
              <div className="mb-3">
                <label className="form-control-label font-weight-bold text-dark d-block">
                  <i className="fas fa-user mr-1 text-primary" /> User Question:
                </label>
                <div
                  className="p-2 p-sm-3 rounded border text-break"
                  style={{
                    backgroundColor: "#f8f9fe",
                    fontSize: "0.92rem",
                    color: "#32325d",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    overflowWrap: "anywhere",
                    maxHeight: "220px",
                    overflowY: "auto",
                  }}
                >
                  {safeString(selectedItem.question) || (
                    <em className="text-muted">No question text captured</em>
                  )}
                </div>
              </div>

              {/* AI Response */}
              <div className="mb-2">
                <div className="d-flex align-items-center justify-content-between mb-1">
                  <label className="form-control-label font-weight-bold text-dark mb-0">
                    <i className="fas fa-robot mr-1 text-tealpot" /> AI Assistant Reply:
                  </label>
                  {selectedItem.reply && (
                    <Button
                      color="link"
                      size="sm"
                      className="p-0 text-tealpot flex-shrink-0"
                      onClick={() => handleCopyText(selectedItem.reply, "AI Reply")}
                    >
                      <i className="far fa-copy mr-1" /> Copy Reply
                    </Button>
                  )}
                </div>
                <div
                  className="p-2 p-sm-3 rounded border text-break"
                  style={{
                    backgroundColor: "#f7fafc",
                    fontSize: "0.92rem",
                    color: "#525f7f",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    overflowWrap: "anywhere",
                    maxHeight: "320px",
                    overflowY: "auto",
                    lineHeight: "1.6",
                  }}
                >
                  {safeString(selectedItem.reply) || (
                    <em className="text-muted">No reply text captured</em>
                  )}
                </div>
              </div>
            </>
          )}
        </ModalBody>
        <ModalFooter className="border-top py-2 px-3 px-md-4 d-flex justify-content-end">
          <Button color="secondary" size="sm" className="px-4" onClick={closeDetailModal}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default ChatbotFeedback;
